import React from 'react';
import {
  AvTooltip,
  FeatureFlags,
  SEVERITY_LABELS,
  severityBackgroundColor,
  SeverityLabels,
  SeverityPrimaryColors,
} from '@fabric-ui/components';
import { dotStyle, flex } from '@fabric-ui/theme';
import { uniqBy } from '@fabric-ui/utils';
import { Box, Divider } from '@mui/material';
import { Cell, XAxis } from 'recharts';
import { useAvContext } from '../../context/AvContextProvider';
import { useSeverity } from '../../views/Settings/SeveritySlaSettings/Severity/useSeverity';
import AvLegend from '../AvLegend';
import { AvComposedWidget } from './AvComposedWidget';

const getRange = (index, isNewRiskScoreEnabled: boolean) => {
  if (index <= 6) {
    return '0';
  }
  if (index > 6 && index <= 12) {
    return isNewRiskScoreEnabled ? '50' : '5';
  }
  return isNewRiskScoreEnabled ? '100' : '10';
};

const getRangeTitle = (index, isNewRiskScoreEnabled: boolean) => {
  if (+index === 0) {
    return 'Min Asset Risk';
  }
  if (+index === (isNewRiskScoreEnabled ? 50 : 5)) {
    return 'Median Asset Risk';
  }
  return 'Max Asset Risk';
};

interface ILabelProps {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
  payload: {
    value: string;
    offset: number;
  };
  isNewRiskScoreEnabled?: boolean;
}

const Label = ({ index, x, y, width, height, payload, isNewRiskScoreEnabled = false }: ILabelProps) => {
  const getWrapperStyle = index => {
    if (index === 0) {
      return flex.colItemsStart;
    }
    if (index === 1) {
      return flex.colItemsCenter;
    }
    return flex.colItemsEnd;
  };
  return (
    <foreignObject x={x - payload.offset} y={y - 5} width={width / 3} height={height / 2}>
      <Box sx={getWrapperStyle(index)}>
        <Box sx={{ fontSize: '13px', fontWeight: 600 }}>
          <AvTooltip title={getRangeTitle(payload.value, isNewRiskScoreEnabled)}>{payload.value}</AvTooltip>
        </Box>
      </Box>
    </foreignObject>
  );
};

interface IActiveAssetsHistogramProps {
  data?: {
    id: string;
    risk: string;
    pureRisk: number;
    assets: number;
  }[];
}

interface HistogramTooltipPayload {
  color: string;
  name: string;
  risk: string;
  assets: number;
}

const HistogramTooltip = ({ payload }: { payload?: Array<{ payload?: HistogramTooltipPayload }> }) => {
  if (!payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  const rows = [
    { label: 'Risk Score', value: item.risk },
    { label: 'Assets', value: item.assets?.toLocaleString() },
  ];
  return (
    <Box
      sx={{
        ...flex.col,
        gap: 0.5,
        background: 'var(--color-surface-immutable-darkGrey)',
        borderRadius: 2.5,
        color: 'var(--color-content-immutable-white)',
        fontSize: 12,
        pb: 1,
      }}>
      <Box sx={{ ...flex.itemsCenter, fontWeight: 600, mb: '2px', px: 1, pt: 1, gap: 1 }}>
        <Box sx={{ ...dotStyle(item.color), border: '1px solid var(--color-border-base-tertiary)', flexShrink: 0 }} />
        <Box sx={{ px: 1 }}>{item.name}</Box>
      </Box>
      {/* @ts-ignore */}
      <Divider variant="center" sx={{ borderColor: 'var(--color-content-immutable-grey)' }} />
      <Box sx={{ ...flex.col, gap: 0.5, px: 1, pt: 0.5 }}>
        {rows.map(({ label, value }) => (
          <Box key={label} sx={{ ...flex.justifyBetween, gap: 3 }}>
            <div>{label}</div>
            <Box sx={{ fontWeight: 600 }}>{value}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ActiveAssetsHistogram: React.FC<IActiveAssetsHistogramProps> = ({ data: d = [] }) => {
  const { getSeverity, getSeverityName } = useSeverity();
  const { featureFlags } = useAvContext();
  const isNewRiskScoreEnabled = featureFlags[FeatureFlags.NewRiskScore];

  const data = d.map((item, index) => {
    const severity = getSeverity(+item.pureRisk);
    const name = getSeverityName(severity || SeverityLabels.Low);
    return {
      ...item,
      name,
      range: getRange(index, isNewRiskScoreEnabled),
      color: severityBackgroundColor(severity!),
    };
  });
  const series = [
    {
      name: data[0].name,
      dataKey: 'assets',
      type: 'bar',
      stackId: 'a',
      minPointSize: 1,
      children: data.map(entry => <Cell key={entry.id} fill={entry.assets === 0 ? 'transparent' : entry.color} />),
    },
    {
      name: 'risk',
      color: 'transparent',
      dataKey: 'risk',
      type: 'bar',
      stackId: 'a',
    },
  ];

  const legendSeries = [
    {
      name: SEVERITY_LABELS.LOW,
      color: SeverityPrimaryColors[SeverityLabels.Low],
    },
    {
      name: SEVERITY_LABELS.MEDIUM,
      color: SeverityPrimaryColors[SeverityLabels.Medium],
    },
    {
      name: SEVERITY_LABELS.HIGH,
      color: SeverityPrimaryColors[SeverityLabels.High],
    },
    {
      name: SEVERITY_LABELS.CRITICAL,
      color: SeverityPrimaryColors[SeverityLabels.Critical],
    },
  ];

  const xAxis = (
    // @ts-ignore
    <XAxis
      xAxisId={1}
      dataKey="range"
      height={40}
      allowDuplicatedCategory={false}
      axisLine={false}
      tickLine={false}
      // @ts-ignore
      tick={<Label isNewRiskScoreEnabled={isNewRiskScoreEnabled} />}
    />
  );

  return (
    <Box sx={{ ...flex.col, height: '100%', width: '100%' }}>
      <Box sx={{ mb: 1.5 }}>
        <AvLegend series={legendSeries} isHorizontal />
      </Box>
      <Box sx={{ height: '100%' }}>
        <AvComposedWidget
          xAxisKey="risk"
          data={data}
          series={uniqBy(series, 'name')}
          labels={{ y: 'Assets' }}
          showLegend={false}
          getTooltipContent={HistogramTooltip}
          tooltipComponentProps={{ shared: true }}
          chartProps={{ margin: { left: 5 } }}
          defaultAxisProps={{ x: { hide: true } }}
          additionalAxis={xAxis}
        />
      </Box>
    </Box>
  );
};

export default ActiveAssetsHistogram;
